import { VIEWPORTS, DESKTOP, getPlayHudReservedZoneHeight } from "./escapeRoomEditorConstants";

export const MOBILE_MIN_TOUCH_PX = 56;
export const MOBILE_MIN_FONT_PX = 17;
export const MOBILE_SIZE_SCALE_BOOST = 1.5;
export const MOBILE_SIZE_SCALE_MIN = 0.58;

function clamp(n, min, max) {
	return Math.min(max, Math.max(min, n));
}

function viewportById(viewportId) {
	return VIEWPORTS.find((v) => v.id === viewportId) || null;
}

export function layoutScaleForViewport(vp) {
	if (!vp) return 1;
	const scaleX = vp.width / DESKTOP.width;
	const scaleY = vp.height / DESKTOP.height;
	const uniform = Math.min(scaleX, scaleY);
	if (vp.id !== "mobile") return uniform;
	return Math.max(MOBILE_SIZE_SCALE_MIN, uniform * MOBILE_SIZE_SCALE_BOOST);
}

function parseFontSizePx(val, fallback = 18) {
	if (typeof val === "number" && Number.isFinite(val)) return val;
	const m = /^(\d+(?:\.\d+)?)px$/i.exec(String(val ?? "").trim());
	return m ? parseFloat(m[1]) : fallback;
}

function isWideDesktopElement(el) {
	const w = Number(el?.width) || 0;
	return w >= DESKTOP.width * 0.42;
}

function mobileFontFloorPx(desktopEl, currentPx) {
	const desktopPx = parseFontSizePx(desktopEl?.style?.fontSize, 18);
	let minPx = MOBILE_MIN_FONT_PX;
	if (desktopPx >= 40) minPx = Math.max(30, Math.round(desktopPx * 0.58));
	else if (desktopPx >= 28) minPx = Math.max(24, Math.round(desktopPx * 0.62));
	else if (desktopPx >= 20) minPx = Math.max(19, Math.round(desktopPx * 0.68));
	else if (desktopPx >= 16) minPx = Math.max(17, Math.round(desktopPx * 0.75));
	return Math.max(minPx, Math.round(currentPx));
}

function mobileButtonFontPx(desktopEl, currentPx) {
	const desktopFs = parseFontSizePx(desktopEl?.style?.fontSize, 16);
	const hasLabel = String(desktopEl?.content?.label || "").trim().length > 0;
	if (!hasLabel) return Math.max(MOBILE_MIN_FONT_PX, Math.round(currentPx));
	if (isWideDesktopElement(desktopEl)) {
		return Math.max(Math.max(20, Math.round(desktopFs * 0.78)), Math.round(currentPx));
	}
	return Math.max(Math.max(18, Math.round(desktopFs * 0.74)), Math.round(currentPx));
}

function mobileFontSizeFromDesktop(desktopEl) {
	const desktopFs = parseFontSizePx(desktopEl?.style?.fontSize, 18);
	const scaled = Math.round(desktopFs * 0.72);
	return desktopEl.type === "button"
		? mobileButtonFontPx(desktopEl, scaled)
		: mobileFontFloorPx(desktopEl, scaled);
}

function plainTextFromHtml(el) {
	const html = el?.content?.html || el?.content?.text || "";
	return String(html).replace(/<[^>]+>/g, " ").replace(/&nbsp;/gi, " ").replace(/\s+/g, " ").trim();
}

const MOBILE_SIDE_MARGIN = 16;
const MOBILE_FLOW_GAP = 14;
const MOBILE_BOTTOM_MARGIN = 18;
const DESKTOP_EDGE_PX = 28;

function wordsFromText(el) {
	const plain = plainTextFromHtml(el);
	return plain.split(/\s+/).filter(Boolean);
}

function charWidthFactor(fontFamily, fontWeight) {
	const f = String(fontFamily || "").toLowerCase();
	if (/impact|anton|bebas/.test(f)) return 0.52;
	if (/league spartan/.test(f)) return 0.54;
	const fw = fontWeight;
	if (fw === "bold" || Number(fw) >= 600) return 0.58;
	return 0.55;
}

function estimateWordWidthPx(word, fontSizePx, fontFamily, fontWeight) {
	return Math.max(1, String(word).length) * fontSizePx * charWidthFactor(fontFamily, fontWeight);
}

/** Reduce fontSize en móvil hasta que la palabra más larga quepa en una línea. */
function mobileTextFontSizeForWidth(desktopEl, boxWidthPx) {
	let fs = mobileFontSizeFromDesktop(desktopEl);
	const words = wordsFromText(desktopEl);
	if (!words.length) return fs;
	const innerW = Math.max(48, boxWidthPx - 16);
	const fontFamily = desktopEl?.style?.fontFamily || "";
	const fontWeight = desktopEl?.style?.fontWeight;
	const longest = words.reduce((a, b) => (a.length >= b.length ? a : b), "");
	while (fs > MOBILE_MIN_FONT_PX && estimateWordWidthPx(longest, fs, fontFamily, fontWeight) > innerW * 0.96) {
		fs -= 1;
	}
	return fs;
}

function estimateTextLinesByWords(desktopEl, width, fontSizePx) {
	const words = wordsFromText(desktopEl);
	if (!words.length) return 1;
	const innerW = Math.max(48, width - 16);
	const fontFamily = desktopEl?.style?.fontFamily || "";
	const fontWeight = desktopEl?.style?.fontWeight;
	const spaceW = fontSizePx * 0.28;
	let lines = 1;
	let lineW = 0;
	for (const word of words) {
		const ww = estimateWordWidthPx(word, fontSizePx, fontFamily, fontWeight);
		if (lineW > 0 && lineW + spaceW + ww > innerW) {
			lines += 1;
			lineW = ww;
		} else {
			lineW = lineW > 0 ? lineW + spaceW + ww : ww;
		}
	}
	return lines;
}

function getDesktopEdgeAnchors(el) {
	const x = Number(el?.x) || 0;
	const y = Number(el?.y) || 0;
	const w = Number(el?.width) || 100;
	const h = Number(el?.height) || 50;
	return {
		left: x <= DESKTOP_EDGE_PX,
		right: x + w >= DESKTOP.width - DESKTOP_EDGE_PX,
		top: y <= DESKTOP_EDGE_PX,
		bottom: y + h >= DESKTOP.height - DESKTOP_EDGE_PX,
		fullWidth: w >= DESKTOP.width * 0.82,
		hCenter: Math.abs(x + w / 2 - DESKTOP.width / 2) <= 48,
	};
}

function applyMobileEdgeLayout(box, desktopEl, vp) {
	if (!desktopEl || !vp || !box) return box;
	const anchors = getDesktopEdgeAnchors(desktopEl);
	let { x, y, width, height } = box;
	const align = String(desktopEl.style?.textAlign || "left").toLowerCase();
	const stickHorizontal =
		anchors.fullWidth
		|| (anchors.left && anchors.right)
		|| (desktopEl.type === "shape" && anchors.left)
		|| (desktopEl.type === "button" && isWideDesktopElement(desktopEl));

	if (stickHorizontal) {
		width = vp.width;
		x = 0;
	} else if (anchors.left && !anchors.right) {
		x = 0;
	} else if (anchors.right && !anchors.left) {
		x = Math.max(0, vp.width - width);
	} else if (anchors.hCenter || align === "center") {
		x = Math.round((vp.width - width) / 2);
	}

	if (anchors.top && (anchors.fullWidth || (anchors.left && anchors.right))) {
		y = 0;
	}

	x = clamp(x, 0, Math.max(0, vp.width - width));
	return { x, y, width, height };
}

function countHtmlBlockLines(el) {
	const html = String(el?.content?.html || el?.content?.text || "");
	if (!html.trim()) return 1;
	const chunks = html
		.split(/<\/p\s*>|<br\s*\/?>/gi)
		.map((part) => part.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim())
		.filter(Boolean);
	return Math.max(1, chunks.length);
}

function estimateTextBlockHeight(desktopEl, width, fontSizePx) {
	const lineH = Number(desktopEl.style?.lineHeight) || 1.5;
	const wordLines = estimateTextLinesByWords(desktopEl, width, fontSizePx);
	const blockLines = countHtmlBlockLines(desktopEl);
	const lines = Math.max(wordLines, blockLines);
	const padding = 24;
	const fromCopy = Math.ceil(lines * fontSizePx * lineH + padding);
	const baseH = Number(desktopEl.height) || 50;
	const baseW = Math.max(1, Number(desktopEl.width) || 100);
	const fromDesktop = Math.round(baseH * (width / baseW) * 0.95);
	return Math.ceil(Math.max(56, fromCopy, fromDesktop) * 1.12) + 6;
}

function scaledSize(el, scale, vp) {
	let w = Math.max(1, Math.round((el.width || 100) * scale));
	let h = Math.max(1, Math.round((el.height || 50) * scale));
	if (vp?.id === "mobile") {
		if (el.type === "button") {
			const hasLabel = String(el.content?.label || "").trim().length > 0;
			h = Math.max(hasLabel ? 56 : MOBILE_MIN_TOUCH_PX, h);
			w = Math.max(MOBILE_MIN_TOUCH_PX, w);
			if (isWideDesktopElement(el)) {
				w = Math.min(vp.width - 32, Math.max(w, vp.width - 48));
			}
		}
	}
	return { width: w, height: h };
}

export function autoLayoutElementForViewport(el, viewportId) {
	if (!el || viewportId === "desktop") return el;
	const vp = viewportById(viewportId);
	if (!vp) return el;

	const baseW = Number(el.width) || 100;
	const baseH = Number(el.height) || 50;
	const baseX = Number(el.x) || 0;
	const baseY = Number(el.y) || 0;

	const cx = (baseX + baseW / 2) / DESKTOP.width;
	const cy = (baseY + baseH / 2) / DESKTOP.height;

	const scale = layoutScaleForViewport(vp);
	const { width, height } = scaledSize(el, scale, vp);

	let x = Math.round(cx * vp.width - width / 2);
	let y = Math.round(cy * vp.height - height / 2);
	x = clamp(x, 0, Math.max(0, vp.width - width));
	y = clamp(y, 0, Math.max(0, vp.height - height));

	return { x, y, width, height, scale };
}

export function viewportDisplayedToDesktop(el, viewportId) {
	if (!el || viewportId === "desktop") {
		return {
			x: Math.round(Number(el?.x) || 0),
			y: Math.round(Number(el?.y) || 0),
			width: Math.max(1, Math.round(Number(el?.width) || 100)),
			height: Math.max(1, Math.round(Number(el?.height) || 50)),
		};
	}
	const vp = viewportById(viewportId);
	if (!vp) {
		return {
			x: Math.round(Number(el?.x) || 0),
			y: Math.round(Number(el?.y) || 0),
			width: Math.max(1, Math.round(Number(el?.width) || 100)),
			height: Math.max(1, Math.round(Number(el?.height) || 50)),
		};
	}

	const displayW = Number(el.width) || 100;
	const displayH = Number(el.height) || 50;
	const displayX = Number(el.x) || 0;
	const displayY = Number(el.y) || 0;

	const cx = (displayX + displayW / 2) / vp.width;
	const cy = (displayY + displayH / 2) / vp.height;

	const scale = layoutScaleForViewport(vp);
	const desktopW = Math.max(1, Math.round(displayW / scale));
	const desktopH = Math.max(1, Math.round(displayH / scale));

	const x = Math.round(cx * DESKTOP.width - desktopW / 2);
	const y = Math.round(cy * DESKTOP.height - desktopH / 2);

	return { x, y, width: desktopW, height: desktopH };
}

export function applyMobileTypographyFloor(el, viewportId, desktopEl = null) {
	if (!el || viewportId !== "mobile") return el;
	const base = desktopEl || el;
	const next = { ...el };
	if (el.style && typeof el.style === "object") {
		const st = { ...el.style };
		if (st.fontSize != null) {
			const current = parseFontSizePx(st.fontSize, parseFontSizePx(base.style?.fontSize, 18));
			st.fontSize = base.type === "button"
				? mobileButtonFontPx(base, current)
				: mobileFontFloorPx(base, current);
		}
		next.style = st;
	}
	if (el.type === "quiz" && el.content && typeof el.content === "object") {
		const c = { ...el.content };
		if (c.quizOptionsStyle && typeof c.quizOptionsStyle === "object") {
			const qos = { ...c.quizOptionsStyle };
			if (typeof qos.fontSize === "number") {
				qos.fontSize = Math.max(MOBILE_MIN_FONT_PX, qos.fontSize);
			}
			c.quizOptionsStyle = qos;
		}
		next.content = c;
	}
	return next;
}

export function enrichMobileViewportElement(desktopEl, viewportEl, vp) {
	if (!viewportEl || !vp || vp.id !== "mobile" || !desktopEl) return viewportEl;

	const next = { ...viewportEl };
	let w = Number(next.width) || 100;
	const x0 = Number(next.x) || 0;

	if (desktopEl.type === "button") {
		const hasLabel = String(next.content?.label ?? desktopEl.content?.label ?? "").trim().length > 0;
		if (hasLabel) {
			next.height = Math.max(56, Number(next.height) || 56);
			w = isWideDesktopElement(desktopEl) || getDesktopEdgeAnchors(desktopEl).fullWidth
				? vp.width
				: Math.max(vp.width - 32, w);
		} else {
			w = Math.max(MOBILE_MIN_TOUCH_PX, w);
			next.height = Math.max(MOBILE_MIN_TOUCH_PX, Number(next.height) || MOBILE_MIN_TOUCH_PX);
		}
		const edged = applyMobileEdgeLayout(
			{ x: Number(next.x) || 0, y: Number(next.y) || 0, width: w, height: Number(next.height) || 56 },
			desktopEl,
			vp
		);
		next.x = edged.x;
		next.width = edged.width;
		next.height = edged.height;
	}

	if (desktopEl.type === "text") {
		const boxW = Number(next.width) || vp.width;
		const fs = mobileTextFontSizeForWidth(desktopEl, boxW);
		next.height = Math.max(Number(next.height) || 48, estimateTextBlockHeight(desktopEl, boxW, fs));
		const edged = applyMobileEdgeLayout(
			{ x: Number(next.x) || 0, y: Number(next.y) || 0, width: boxW, height: next.height },
			desktopEl,
			vp
		);
		next.x = edged.x;
		next.width = edged.width;
		next.height = edged.height;
	}

	if (desktopEl.type === "hint") {
		const edged = applyMobileEdgeLayout(
			{ x: 0, y: Number(next.y) || 0, width: vp.width, height: Math.max(60, Number(next.height) || 60) },
			desktopEl,
			vp
		);
		next.x = edged.x;
		next.width = edged.width;
		next.height = edged.height;
	}

	if (next.style && typeof next.style === "object") {
		const st = { ...next.style };
		if (desktopEl.type === "text") {
			st.fontSize = mobileTextFontSizeForWidth(desktopEl, Number(next.width) || vp.width);
		} else {
			st.fontSize = mobileFontSizeFromDesktop(desktopEl);
		}
		if (desktopEl.type === "button") {
			if (st.borderRadiusPercent != null) {
				st.borderRadiusPercent = Math.max(Number(st.borderRadiusPercent) || 0, 14);
			} else if (typeof st.borderRadius === "number") {
				st.borderRadius = Math.max(st.borderRadius, 14);
			}
		}
		next.style = st;
	}

	return applyMobileTypographyFloor(next, "mobile", desktopEl);
}

function mergeStyleContentFromBreakpoint(desktopEl, viewportId = "mobile") {
	const override = desktopEl?.breakpoints?.[viewportId];
	if (!override) return desktopEl;
	return {
		...desktopEl,
		...(override.content && { content: { ...(desktopEl.content || {}), ...override.content } }),
		...(override.style && { style: { ...(desktopEl.style || {}), ...override.style } }),
	};
}

function isPinnedTopElement(el) {
	if (!el) return false;
	const anchors = getDesktopEdgeAnchors(el);
	const spansWidth = anchors.fullWidth || (anchors.left && anchors.right);
	if (!anchors.top || !spansWidth) return false;
	if (el.type === "shape") return true;
	if (el.type === "image" || el.type === "video") return true;
	return false;
}

function isTopChromeShape(el) {
	return isPinnedTopElement(el);
}

function isBottomCtaButton(el) {
	if (el?.type !== "button") return false;
	const y = Number(el.y) || 0;
	const h = Number(el.height) || 0;
	return y + h >= DESKTOP.height - 95;
}

function mobileReadingOrder(a, b) {
	const ay = Number(a.y) || 0;
	const by = Number(b.y) || 0;
	const ax = Number(a.x) || 0;
	const bx = Number(b.x) || 0;
	const ah = Number(a.height) || 50;
	const bh = Number(b.height) || 50;
	const overlap = Math.min(ay + ah, by + bh) - Math.max(ay, by);
	const minH = Math.min(ah, bh);
	if (minH > 0 && overlap > minH * 0.3) return ax - bx;
	const dy = ay - by;
	if (Math.abs(dy) > 8) return dy;
	return ax - bx;
}

function layoutFlowItem(desktopEl, vp, scale) {
	const contentWidth = vp.width - 2 * MOBILE_SIDE_MARGIN;
	const anchors = getDesktopEdgeAnchors(desktopEl);
	let w;
	let h;
	let x = MOBILE_SIDE_MARGIN;

	if (desktopEl.type === "text") {
		if (anchors.fullWidth || anchors.hCenter || (anchors.left && anchors.right) || anchors.left) {
			w = vp.width;
			x = 0;
		} else {
			w = contentWidth;
		}
		const fs = mobileTextFontSizeForWidth(desktopEl, w);
		h = estimateTextBlockHeight(desktopEl, w, fs);
	} else if (desktopEl.type === "quiz") {
		w = anchors.fullWidth || (anchors.left && anchors.right) ? vp.width : contentWidth;
		x = w >= vp.width ? 0 : MOBILE_SIDE_MARGIN;
		h = Math.max(120, Math.round((Number(desktopEl.height) || 200) * scale * 1.1));
	} else if (desktopEl.type === "hint" || desktopEl.type === "button") {
		w = isWideDesktopElement(desktopEl) || anchors.fullWidth ? vp.width : contentWidth;
		x = w >= vp.width ? 0 : MOBILE_SIDE_MARGIN;
		h = Math.max(60, Math.round((Number(desktopEl.height) || 56) * scale * 1.2));
		if (desktopEl.type === "button" && !String(desktopEl.content?.label || "").trim()) {
			w = Math.min(88, Math.max(MOBILE_MIN_TOUCH_PX, Math.round((Number(desktopEl.width) || 70) * scale * 1.2)));
			x = Math.round((vp.width - w) / 2);
		}
	} else if (desktopEl.type === "image" || desktopEl.type === "video") {
		w = anchors.fullWidth || anchors.left ? vp.width : contentWidth;
		x = w >= vp.width ? 0 : MOBILE_SIDE_MARGIN;
		const ar = (Number(desktopEl.height) || 200) / Math.max(1, Number(desktopEl.width) || 300);
		h = Math.max(100, Math.round(w * ar));
	} else if (desktopEl.type === "shape") {
		if (anchors.fullWidth || (anchors.left && anchors.right)) {
			w = vp.width;
			x = 0;
		} else {
			w = Math.min(contentWidth, Math.max(40, Math.round((Number(desktopEl.width) || 100) * scale)));
		}
		h = Math.max(8, Math.round((Number(desktopEl.height) || 50) * scale));
	} else {
		const sized = scaledSize(desktopEl, scale, vp);
		w = sized.width;
		h = sized.height;
		x = clamp(Math.round((Number(desktopEl.x) / DESKTOP.width) * vp.width), MOBILE_SIDE_MARGIN, vp.width - w - MOBILE_SIDE_MARGIN);
	}

	return applyMobileEdgeLayout({ x, y: 0, width: w, height: h }, desktopEl, vp);
}

function applyMobileElementTypography(desktopEl, el) {
	const next = { ...el };
	if (!next.style || typeof next.style !== "object") {
		next.style = { ...(desktopEl.style || {}) };
	}
	const st = { ...next.style };
	const boxW = Number(el.width) || DESKTOP.width;
	if (desktopEl.type === "text") {
		st.fontSize = mobileTextFontSizeForWidth(desktopEl, boxW);
	} else {
		st.fontSize = mobileFontSizeFromDesktop(desktopEl);
	}
	if (typeof st.borderRadius === "number" && desktopEl.type === "button") {
		st.borderRadius = Math.max(st.borderRadius, 14);
	}
	next.style = st;
	let result = applyMobileTypographyFloor(next, "mobile", desktopEl);
	if (desktopEl.type === "text") {
		const fs = parseFontSizePx(result.style?.fontSize, 18);
		result = {
			...result,
			height: Math.max(Number(result.height) || 0, estimateTextBlockHeight(desktopEl, boxW, fs)),
		};
	}
	return result;
}

/**
 * Apila en vertical para móvil. Solo aplica a elementos sin breakpoint manual
 * (getElementsForViewport usa breakpoints.mobile.x cuando el usuario editó en móvil).
 */
export function layoutMobilePageElements(desktopElements, vp, _applyTypographyScale) {
	const scale = layoutScaleForViewport(vp);
	const indexed = (desktopElements || []).map((el, index) => ({
		index,
		key: el.elementId || `__idx_${index}`,
		desktop: el,
		merged: mergeStyleContentFromBreakpoint(el),
	}));
	const positions = new Map();
	const hudTop = getPlayHudReservedZoneHeight("mobile");

	const topChrome = [];
	const bottomCta = [];
	const flow = [];

	for (const item of indexed) {
		const el = item.merged;
		if (isPinnedTopElement(el)) topChrome.push(item);
		else if (isBottomCtaButton(el)) bottomCta.push(item);
		else flow.push(item);
	}

	flow.sort((a, b) => mobileReadingOrder(a.merged, b.merged));

	let topBarBottom = 0;
	for (const item of topChrome) {
		const el = item.merged;
		const h = Math.max(4, Math.round((Number(el.height) || 5) * scale * 1.1));
		positions.set(item.key, { x: 0, y: 0, width: vp.width, height: h });
		topBarBottom = Math.max(topBarBottom, h);
	}

	const bottomH = bottomCta.length > 0
		? Math.max(56, ...bottomCta.map((b) => Math.max(56, Math.round((Number(b.merged.height) || 44) * scale * 1.1))))
		: 0;
	const bottomReserved = bottomH + MOBILE_BOTTOM_MARGIN + 10;

	const flowBoxes = flow.map((item) => ({
		item,
		box: layoutFlowItem(item.merged, vp, scale),
	}));

	const gaps = Math.max(0, flowBoxes.length - 1) * MOBILE_FLOW_GAP;
	const totalFlowH = flowBoxes.reduce((sum, { box }) => sum + box.height, 0) + gaps;
	const contentTop = topBarBottom > 0 ? topBarBottom + MOBILE_FLOW_GAP : hudTop + 6;
	const contentBottom = vp.height - bottomReserved;
	const available = Math.max(120, contentBottom - contentTop);

	let cursorY = contentTop;
	if (totalFlowH < available * 0.92) {
		cursorY = contentTop + Math.round((available - totalFlowH) * 0.22);
	}

	let gap = MOBILE_FLOW_GAP;
	if (totalFlowH > available && flowBoxes.length > 1) {
		gap = Math.max(6, Math.floor((available - flowBoxes.reduce((s, { box }) => s + box.height, 0)) / (flowBoxes.length - 1)));
	}

	for (const { item, box } of flowBoxes) {
		const maxY = contentBottom - box.height;
		box.y = clamp(cursorY, contentTop, Math.max(contentTop, maxY));
		positions.set(item.key, { x: box.x, y: box.y, width: box.width, height: box.height });
		cursorY = box.y + box.height + gap;
	}

	for (let i = 0; i < bottomCta.length; i++) {
		const item = bottomCta[bottomCta.length - 1 - i];
		const h = bottomH || 56;
		const y = vp.height - h - MOBILE_BOTTOM_MARGIN - i * (h + 8);
		positions.set(item.key, { x: 0, y, width: vp.width, height: h });
	}

	return indexed.map(({ key, desktop, merged }) => {
		const pos = positions.get(key);
		const positioned = pos ? { ...merged, ...pos } : merged;
		const enriched = enrichMobileViewportElement(desktop, positioned, vp);
		return applyMobileElementTypography(desktop, enriched);
	});
}
